/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { DeviceWorkability } from '../../../../../data/api/devices/devicesClient.typing';
import { SeriesItemConfig } from '../../../../Charts/Common/ChartsTyping';
import { COLOR_BY_WORKABILITY } from '../../DevicesList/DevicesListConfig';
import { StackedBarItemData } from './StackedBarWithValueMark';

export interface WorkabilityChartLevels {
    new: number;
    junior: number;
    senior: number;
    veteran: number;
    invalid: number;
}

export interface Border {
    min: number;
    max: number;
}

export const STACKED_BAR_HEIGHT = 30;
export const WEAROUT_DELTA = 500000;
export const YEAR = 365;
export const AGE_DELTA = 1 * YEAR;
export const EXPERIENCE_DELTA = 1 * YEAR;

export const WEAROUT_BORDERS_BY_WORKABYLITY_LEVEL: { [key in keyof WorkabilityChartLevels]: Border } = {
    new: {
        min: 0,
        max: 0,
    },
    junior: {
        min: 0,
        max: 1500000,
    },
    senior: {
        min: 1500000,
        max: 3000000,
    },
    veteran: {
        min: 3000000,
        max: 4000000,
    },
    invalid: {
        min: 4000000,
        max: 5000000,
    },
};

export const AGE_BORDERS_BY_WORKABYLITY_LEVEL: { [key in keyof WorkabilityChartLevels]: Border } = {
    new: {
        min: 0,
        max: 2 * YEAR,
    },
    junior: {
        min: 2 * YEAR,
        max: 6 * YEAR,
    },
    senior: {
        min: 6 * YEAR,
        max: 8 * YEAR,
    },
    veteran: {
        min: 8 * YEAR,
        max: 10 * YEAR,
    },
    invalid: {
        min: 10 * YEAR,
        max: 12 * YEAR,
    },
};

export const EXPERIENCE_BORDERS_BY_WORKABYLITY_LEVEL: { [key in keyof WorkabilityChartLevels]: Border } = {
    new: {
        min: 0,
        max: 0,
    },
    junior: {
        min: 0,
        max: 2 * YEAR,
    },
    senior: {
        min: 2 * YEAR,
        max: 4 * YEAR,
    },
    veteran: {
        min: 4 * YEAR,
        max: 6 * YEAR,
    },
    invalid: {
        min: 6 * YEAR,
        max: 8 * YEAR,
    },
};

export const workabilityChartConfig: SeriesItemConfig<WorkabilityChartLevels>[] = [
    {
        keyForValue: 'new',
        color: COLOR_BY_WORKABILITY[DeviceWorkability.NEW],
        translatePath: 'device.workabilityLevels.NEW',
    },
    {
        keyForValue: 'junior',
        color: COLOR_BY_WORKABILITY[DeviceWorkability.JUNIOR],
        translatePath: 'device.workabilityLevels.JUNIOR',
    },
    {
        keyForValue: 'senior',
        color: COLOR_BY_WORKABILITY[DeviceWorkability.SENIOR],
        translatePath: 'device.workabilityLevels.SENIOR',
    },
    {
        keyForValue: 'veteran',
        color: COLOR_BY_WORKABILITY[DeviceWorkability.VETERAN],
        translatePath: 'device.workabilityLevels.VETERAN',
    },
    {
        keyForValue: 'invalid',
        color: COLOR_BY_WORKABILITY[DeviceWorkability.INVALID],
        translatePath: 'device.workabilityLevels.INVALID',
    },
];

export const createWorkabilityChartData = (
    value: number,
    delta: number,
    borders: { [key in keyof WorkabilityChartLevels]: Border },
): StackedBarItemData[] => {
    const data: StackedBarItemData[] = workabilityChartConfig.map((item) => {
        if (item.keyForValue === 'invalid' && value > borders[item.keyForValue].max) {
            const max = Math.ceil(value / delta) * delta;
            return {
                length: max - borders[item.keyForValue].min,
                color: item.color,
                minBorder: borders[item.keyForValue].min,
            };
        } else
            return {
                length: borders[item.keyForValue].max - borders[item.keyForValue].min,
                color: item.color,
                minBorder: borders[item.keyForValue].min,
            };
    });
    return data;
};
